import type { Root } from 'react-dom/client';

import {
  HYDRATED_DATA_ATTRIBUTE,
  HYDRATE_EVENT_NAME,
  CONTENT_DATA_ATTRIBUTE,
} from 'constants/render';
import { logger } from 'logger';

type Hydrator = (element: HTMLElement) => Root | null | Promise<Root | null>;

interface IslandControllerState {
  /** The single registered hydrator for fragments */
  hydrator: Hydrator | null;
  /** Islands that have been successfully hydrated */
  hydratedIslands: Map<HTMLElement, Root>;
  /** Islands currently mid-hydration. Prevents overlapping async scans */
  pendingIslands: Set<HTMLElement>;
  /** Whether a scan has been scheduled but not yet executed */
  scanScheduled: boolean;
}

const state: IslandControllerState = {
  hydrator: null,
  hydratedIslands: new Map(),
  pendingIslands: new Set(),
  scanScheduled: false,
};

const unmountDisconnectedIslands = (): void => {
  for (const [element, root] of state.hydratedIslands) {
    if (!element.isConnected) {
      root.unmount();
      state.hydratedIslands.delete(element);
    }
  }
};

const scan = async (): Promise<void> => {
  unmountDisconnectedIslands();

  const { hydrator } = state;
  if (!hydrator) return;

  const unhydratedIslands = document.querySelectorAll<HTMLElement>(
    `[${CONTENT_DATA_ATTRIBUTE}][${HYDRATED_DATA_ATTRIBUTE}="false"]`,
  );

  const islandsToHydrate: HTMLElement[] = [];

  for (const island of unhydratedIslands) {
    if (!state.pendingIslands.has(island)) {
      state.pendingIslands.add(island);
      islandsToHydrate.push(island);
    }
  }

  if (islandsToHydrate.length === 0) return;

  await Promise.allSettled(
    islandsToHydrate.map(async island => {
      try {
        const root = await hydrator(island);

        if (root !== null) {
          state.hydratedIslands.set(island, root);
          island.setAttribute(HYDRATED_DATA_ATTRIBUTE, 'true');
        }
      } catch (err) {
        logger.error(
          Object.assign(err instanceof Error ? err : new Error(String(err)), {
            logger: 'ContentRenderer | islandController',
            section: 'Hydration Error',
          }),
        );
      } finally {
        state.pendingIslands.delete(island);
      }
    }),
  );
};

const scheduleScan = (): void => {
  if (state.scanScheduled) return;
  state.scanScheduled = true;

  queueMicrotask(async () => {
    state.scanScheduled = false;
    await scan();
  });
};

const initialise = (hydrator: Hydrator): void => {
  if (state.hydrator) return;

  state.hydrator = hydrator;
  window.addEventListener(HYDRATE_EVENT_NAME, scheduleScan);
  scheduleScan();
};

export const islandController = {
  initialise,
  reset: () => {
    window.removeEventListener(HYDRATE_EVENT_NAME, scheduleScan);
    state.hydrator = null;
    state.scanScheduled = false;
    state.hydratedIslands.clear();
    state.pendingIslands.clear();
  },
};
